import {
  AfterViewChecked,
  Component,
  ElementRef,
  HostBinding,
  Input,
  OnInit,
  Renderer2,
} from '@angular/core';
import { BaseControlComponent } from './base-control.component';
import { srSpeak } from 'src/app/shared';
import { DatePipe } from '@angular/common';
import { TranslationRequest } from 'src/app/shared/models/translation/translation-request';
import { DateAdapter } from '@angular/material/core';

@Component({
    selector: 'app-date-control',
    template: `
    <div class="app-pr1 app-pb1 datepick">
      <mat-form-field
        [hintLabel]="hintLabel | translation : translationRequest"
        [formGroup]="formGroup"
        >
        <mat-label>{{ label | translation : translationRequest }}</mat-label>
        <input
          matInput
          #input
          [placeholder]="placeHolder"
          [formControlName]="formControlName"
          [matDatepicker]="picker"
          autocomplete="off"
          [min]="minValue"
          [max]="maxValue"
          (dateChange)="onDateChange($event)"
          (blur)="onBlur()"
          />
          <mat-datepicker-toggle matSuffix [for]="picker"></mat-datepicker-toggle>
          <mat-datepicker #picker [startAt]="startAt"></mat-datepicker>
    
          @if (!formControl.valid) {
            <mat-error
              class="errormsg"
              [ngClass]="errormsgClass"
              [attr.aria-label]="label + ' is ' + errorMessage"
              >{{ errorMessage | translation : translationRequest }}</mat-error
              >
          }
          @if (formControl.hasError('matDatepickerMin')) {
            <mat-error>{{
              'Date should be inferior' | translation : translationRequest
            }}</mat-error>
          }
        </mat-form-field>
      </div>
    `,
    styles: [
        `
      :host {
        display: block;
      }

      .datepick mat-label {
        color: #67707a;
      }
      /* TODO(mdc-migration): The following rule targets internal classes of form-field that may no longer apply for the MDC version. */
      .datepick::ng-deep .mat-form-field-outline {
        color: var(--sed-dynamic-black-color);
      }

      .datepick:focus mat-label {
        color: var(--sed-dynamic-black-color);
      }
      /* TODO(mdc-migration): The following rule targets internal classes of form-field that may no longer apply for the MDC version. */
      .datepick::ng-deep .mat-form-field-outline-thick .mat-focused {
        color: var(--sed-dynamic-black-color);
      }
      .errormsg {
        color: var(--sed-status-red-color);
      }
      .errormsgabsence {
        color: var(--mat-theme-error, #f44336) !important;
      }
      /* TODO(mdc-migration): The following rule targets internal classes of form-field that may no longer apply for the MDC version. */
      .datepick::ng-deep
        .mat-form-field-appearance-outline.mat-form-field-invalid.mat-form-field-invalid
        .mat-form-field-outline-thick {
        color: #c24533 !important;
      }
      /* TODO(mdc-migration): The following rule targets internal classes of form-field that may no longer apply for the MDC version. */
      .datepick::ng-deep
        .mat-form-field-appearance-outline.mat-form-field-invalid.mat-form-field-invalid
        .mat-form-field-outline-thick:hover {
        color: #3f464d !important;
      }
      ::ng-deep .mat-datepicker-toggle-default-icon {
        color: var(--sed-dynamic-black-color);
      }
    `,
    ],
    standalone: false
})
export class DateControlComponent
  extends BaseControlComponent
  implements OnInit, AfterViewChecked
{
  @HostBinding('attr.ngDefaultControl')
  ngDefaultControl = '';

  @Input()
  errormsgClass: string = '';

  @Input()
  minValue: Date;

  @Input()
  maxValue: Date;

  @Input()
  startAt: Date;

  translationRequest = new TranslationRequest({
    language: sessionStorage.getItem('languagepreference'),
    key: null,
    contNum: 0,
    tableName: null,
    primaryKey: null,
    primaryValue: null,
    lob: null,
    group: 'Shared',
    subGroup: 'Shared',
  });

  constructor(
    private renderer: Renderer2,
    private el: ElementRef,
    private datePipe: DatePipe,
    private adapter: DateAdapter<any>
  ) {
    super();
  }

  ngOnInit(): void {
    const locale = sessionStorage.getItem('languagepreference') || 'en';
    this.adapter.setLocale(locale);
  }

  ngAfterViewChecked(): void {
    this.removeAriaPressed();
    this.setAriaCurrent();
  }

  private removeAriaPressed(): void {
    const buttons = document.querySelectorAll('button[aria-pressed]');
    if (buttons.length > 0) {
      buttons.forEach((button) => {
        button.removeAttribute('aria-pressed');
        button.setAttribute('aria-label', button.getAttribute('title') || '');
        button.removeAttribute('title');
      });
    }
  }

  private setAriaCurrent(): void {
    const selectedDateButton = this.el.nativeElement.querySelector(
      '.mat-calendar-body-selected button'
    );
    if (selectedDateButton) {
      this.renderer.setAttribute(selectedDateButton, 'aria-current', 'date');
    }
  }

  onDateChange(event: any): void {
    const selectedDate = event.value;
    if (selectedDate) {
      const formattedDate = this.datePipe.transform(selectedDate, 'fullDate');
      
      // Debug: Check what's actually stored in the form control
      console.log('[DateControl] onDateChange - Form control value:', {
        eventValue: selectedDate,
        eventValueType: typeof selectedDate,
        isDate: selectedDate instanceof Date,
        formControlValue: this.formControl.value,
        formControlValueType: typeof this.formControl.value,
        formControlIsDate: this.formControl.value instanceof Date
      });
      
      // Ensure the form control has a Date object, not a string
      if (selectedDate instanceof Date && !(this.formControl.value instanceof Date)) {
        console.log('[DateControl] Correcting form control value to Date object');
        this.formControl.setValue(selectedDate, { emitEvent: false });
      }
      
      srSpeak(`Selected date is ${formattedDate}`, 'polite');
    }
  }

  onBlur(): void {
    // When user finishes typing, ensure we have a Date object
    const value = this.formControl.value;
    console.log('[DateControl] onBlur - Current value:', {
      value: value,
      type: typeof value,
      isDate: value instanceof Date
    });
    
    // If it's a string, try to parse it
    if (typeof value === 'string' && value.trim()) {
      const parsedDate = this.adapter.parse(value, []);
      if (parsedDate) {
        console.log('[DateControl] onBlur - Parsed string to Date:', parsedDate);
        this.formControl.setValue(parsedDate, { emitEvent: true });
      }
    }
  }
}
